import type { MessageKey } from '@freightapi/i18n'
import {
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  formatBytes,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
} from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { type ReactNode, useCallback, useState } from 'react'
import { ResourceView } from '../components/ResourceView'
import {
  asApiError,
  type BillingAccount,
  type BillingInvoice,
  type BillingPayload,
  type BillingStatus,
  type BillingTier,
  fetchBilling,
  type InvoiceStatus,
  openBillingPortal,
  type UsageCounters,
  type UsageDay,
  type UsageWindow,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useMode } from '../lib/mode'
import { useResource } from '../lib/resource'
import { useCan } from '../lib/session'

/**
 * Billing (M5-06, scope 3): what this organisation used, what it is on, what it was invoiced, and
 * the one button that leads to the provider.
 *
 * The screen deliberately shows the counters before the invoices. The number a developer wants to
 * check at the end of the month is "how many consignment notes did we issue", and that number is
 * ours — the invoice built on it belongs to the payment provider and can only be linked to, never
 * reproduced here. Where the deployment has no provider at all (`configured: false`) the usage half
 * still stands on its own, because metering is a fact about the platform and billing is a fact
 * about the deployment.
 *
 * The mode switch applies to the usage table only. Test-mode usage is counted and shown but never
 * charged, so it is labelled rather than hidden: a sandbox that quietly reports zero is exactly how
 * a developer learns to distrust the meter (docs/verdienmodel.md).
 */
export const Route = createFileRoute('/_app/billing')({ component: Billing })

const TIER_LABEL = {
  sandbox: 'billing.tier.sandbox',
  starter: 'billing.tier.starter',
  scale: 'billing.tier.scale',
  enterprise: 'billing.tier.enterprise',
} as const satisfies Record<BillingTier, MessageKey<'console'>>

const STATUS_LABEL = {
  active: 'billing.status.active',
  past_due: 'billing.status.past_due',
  canceled: 'billing.status.canceled',
} as const satisfies Record<BillingStatus, MessageKey<'console'>>

const INVOICE_STATUS_LABEL = {
  draft: 'billing.invoices.status.draft',
  open: 'billing.invoices.status.open',
  paid: 'billing.invoices.status.paid',
  uncollectible: 'billing.invoices.status.uncollectible',
  void: 'billing.invoices.status.void',
} as const satisfies Record<InvoiceStatus, MessageKey<'console'>>

function Billing() {
  const { t } = useConsoleI18n()
  const { mode } = useMode()
  const can = useCan()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const load = useCallback(
    (signal: AbortSignal): Promise<BillingPayload> => fetchBilling(mode, signal),
    [mode],
  )
  const { state } = useResource(load)

  // The portal URL is one-time and short-lived, so it is fetched on the click and navigated to
  // straight away; storing it would only create a link that is already dead when it is used.
  const toPortal = useCallback(async () => {
    setBusy(true)
    setError(undefined)
    try {
      const { url } = await openBillingPortal()
      window.location.assign(url)
    } catch (cause) {
      const failure = asApiError(cause)
      setError(failure.problem?.detail ?? t('billing.portal.failed'))
    } finally {
      setBusy(false)
    }
  }, [t])

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-semibold text-display text-ink">{t('nav.billing.label')}</h1>
      <p className="text-body text-muted">{t('billing.intro')}</p>

      {error === undefined ? null : (
        <p role="alert" className="text-body text-danger">
          {error}
        </p>
      )}

      <ResourceView state={state}>
        {({ billing, usage, invoices }) => (
          <div className="flex flex-col gap-6">
            <Plan billing={billing} />
            {billing.configured ? null : (
              <Panel
                title={t('billing.unconfigured.title')}
                body={t('billing.unconfigured.body')}
              />
            )}
            <Usage usage={usage} />
            <Invoices invoices={invoices} />
            {can('organization:update') ? (
              <Panel title={t('billing.portal.title')} body={t('billing.portal.body')}>
                <div className="flex flex-col gap-2">
                  <div>
                    <Button disabled={busy || !billing.configured} onClick={() => void toPortal()}>
                      {t('billing.portal.button')}
                    </Button>
                  </div>
                  {billing.configured ? null : (
                    <p className="text-caption text-muted">{t('billing.portal.unavailable')}</p>
                  )}
                </div>
              </Panel>
            ) : null}
          </div>
        )}
      </ResourceView>
    </section>
  )
}

/** Tier, status and the end of the running period: the three facts an invoice depends on. */
function Plan({ billing }: { readonly billing: BillingAccount }) {
  const { t, locale } = useConsoleI18n()

  return (
    <section
      aria-labelledby="billing-plan"
      className="flex flex-col gap-3 rounded-card border border-line p-4"
    >
      <h2 id="billing-plan" className="font-semibold text-ink text-lead">
        {t('billing.plan.title')}
      </h2>
      <dl className="grid gap-4 sm:grid-cols-3">
        <Fact label={t('billing.plan.tier')} value={t(TIER_LABEL[billing.tier])} />
        <Fact label={t('billing.plan.status')} value={t(STATUS_LABEL[billing.status])} />
        <Fact
          label={t('billing.plan.period_end')}
          value={
            billing.current_period_end === null
              ? t('billing.plan.period_end.none')
              : formatDateTime(billing.current_period_end, locale)
          }
        />
      </dl>
    </section>
  )
}

/** The counters per UTC day, plus the total of the window the API answered with. */
function Usage({ usage }: { readonly usage: UsageWindow }) {
  const { t, locale } = useConsoleI18n()

  const columns: readonly DataTableColumn<UsageDay>[] = [
    {
      key: 'day',
      header: t('billing.column.day'),
      cell: (row) => <span className="text-ink">{formatDate(row.day, locale)}</span>,
    },
    {
      key: 'shipments_issued',
      header: t('billing.column.shipments_issued'),
      numeric: true,
      cell: (row) => formatNumber(row.shipments_issued, locale),
    },
    {
      key: 'signatures',
      header: t('billing.column.signatures'),
      numeric: true,
      cell: (row) => formatNumber(row.signatures, locale),
    },
    {
      key: 'notifications',
      header: t('billing.column.notifications'),
      numeric: true,
      cell: (row) => formatNumber(row.notifications, locale),
    },
    {
      key: 'documents',
      header: t('billing.column.documents'),
      numeric: true,
      cell: (row) => formatNumber(row.documents, locale),
    },
    {
      key: 'storage',
      header: t('billing.column.storage'),
      numeric: true,
      cell: (row) => formatBytes(row.storage_bytes, locale),
    },
  ]

  return (
    <section aria-labelledby="billing-usage" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="billing-usage" className="font-semibold text-ink text-lead">
          {t('billing.usage.title')}
        </h2>
        <p className="text-caption text-muted">
          {t('billing.usage.window', {
            from: formatDate(usage.from, locale),
            to: formatDate(usage.to, locale),
          })}
        </p>
      </div>
      {usage.billable ? null : (
        <p className="text-body text-muted">{t('billing.usage.test_mode')}</p>
      )}
      <Totals totals={usage.totals} />
      <DataTable
        caption={t('billing.usage.table.caption')}
        columns={columns}
        rows={usage.days}
        rowKey={(row) => row.day}
        empty={
          <EmptyState
            title={t('billing.usage.empty.title')}
            description={t('billing.usage.empty.body')}
          />
        }
      />
    </section>
  )
}

/** The window added up, as the five numbers the invoice is built from. */
function Totals({ totals }: { readonly totals: UsageCounters }) {
  const { t, locale } = useConsoleI18n()

  return (
    <section
      aria-labelledby="billing-totals"
      className="flex flex-col gap-3 rounded-card border border-line p-4"
    >
      <h3 id="billing-totals" className="font-medium text-ink">
        {t('billing.totals.title')}
      </h3>
      <dl className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Fact
          label={t('billing.column.shipments_issued')}
          value={formatNumber(totals.shipments_issued, locale)}
        />
        <Fact
          label={t('billing.column.signatures')}
          value={formatNumber(totals.signatures, locale)}
        />
        <Fact
          label={t('billing.column.notifications')}
          value={formatNumber(totals.notifications, locale)}
        />
        <Fact
          label={t('billing.column.documents')}
          value={formatNumber(totals.documents, locale)}
        />
        <Fact
          label={t('billing.column.storage')}
          value={formatBytes(totals.storage_bytes, locale)}
          hint={t('billing.totals.storage.hint')}
        />
      </dl>
    </section>
  )
}

/** What the provider invoiced, as a list that links out rather than a document rendered here. */
function Invoices({ invoices }: { readonly invoices: readonly BillingInvoice[] }) {
  const { t, locale } = useConsoleI18n()

  const columns: readonly DataTableColumn<BillingInvoice>[] = [
    {
      key: 'number',
      header: t('billing.invoices.column.number'),
      cell: (row) => (
        <span className="font-mono text-caption text-ink">{row.number ?? row.id}</span>
      ),
    },
    {
      key: 'status',
      header: t('billing.invoices.column.status'),
      cell: (row) => t(INVOICE_STATUS_LABEL[row.status]),
    },
    {
      key: 'amount',
      header: t('billing.invoices.column.amount'),
      numeric: true,
      cell: (row) => formatMoney(row.amount_due, row.currency, locale),
    },
    {
      key: 'period',
      header: t('billing.invoices.column.period'),
      cell: (row) =>
        row.period_end === null ? (
          <span className="text-muted">—</span>
        ) : (
          formatDate(row.period_end, locale)
        ),
    },
    {
      key: 'link',
      header: t('billing.invoices.column.link'),
      cell: (row) =>
        row.hosted_url === null ? (
          <span className="text-muted">—</span>
        ) : (
          <a
            href={row.hosted_url}
            target="_blank"
            rel="noreferrer"
            className="text-brand-700 underline"
          >
            {t('billing.invoices.open')}
          </a>
        ),
    },
  ]

  return (
    <section aria-labelledby="billing-invoices" className="flex flex-col gap-3">
      <h2 id="billing-invoices" className="font-semibold text-ink text-lead">
        {t('billing.invoices.title')}
      </h2>
      <DataTable
        caption={t('billing.invoices.table.caption')}
        columns={columns}
        rows={invoices}
        rowKey={(row) => row.id}
        empty={
          <EmptyState
            title={t('billing.invoices.empty.title')}
            description={t('billing.invoices.empty.body')}
          />
        }
      />
    </section>
  )
}

function Fact({
  label,
  value,
  hint,
}: {
  readonly label: string
  readonly value: string
  readonly hint?: string
}) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-caption text-muted">{label}</dt>
      <dd className="font-medium text-ink">{value}</dd>
      {hint === undefined ? null : <dd className="text-caption text-muted">{hint}</dd>}
    </div>
  )
}

function Panel({
  title,
  body,
  children,
}: {
  readonly title: string
  readonly body: string
  readonly children?: ReactNode
}) {
  return (
    <section className="flex flex-col gap-3 rounded-card border border-line p-4">
      <h2 className="font-semibold text-ink text-lead">{title}</h2>
      <p className="text-body text-muted">{body}</p>
      {children}
    </section>
  )
}
